// Friend links — https://app.seabearing.com/j#friend=<token>. A signed-in user makes one (see
// ./friends) and sends it however they like; whoever opens it is asked "Become friends with <name>?".
//
// This module is the URL side only, and deliberately tiny (no Supabase import): main.ts runs
// captureFriendLink() BEFORE the login wall, so the link survives it. The token starts in the URL
// fragment (not sent to the server), is lifted into localStorage and the address bar is cleaned (a
// reload doesn't re-trigger it and the token doesn't linger in history). It has to be localStorage,
// not the URL: a magic-link / OAuth sign-in redirects back to origin + path with the fragment gone
// (auth/store redirectTo). After sign-in the app reads the stash, asks, and clears it once the
// question is settled.

import { clearPendingSeabearingJoinPayload, pendingSeabearingJoinPayload, seabearingJoinUrl } from '../crew/qr';

const FRIEND_PARAM = 'friend';
const PENDING_KEY = 'seabearing:friend:pending';
// A stashed link older than this is dropped unasked (links themselves expire after 7 days; the server
// is the authority on validity — this only bounds how long an unanswered stash can sit).
const PENDING_TTL_MS = 30 * 24 * 3600 * 1000;
// Server tokens are 32 base64url chars; accept a looser shape so a mangled link still reaches the
// server and gets a plain "isn't valid" instead of silently vanishing.
const TOKEN_RE = /^[A-Za-z0-9_-]{8,128}$/;

/** The shareable link for a token. */
export function friendLinkUrl(token: string): string {
  return seabearingJoinUrl(`${FRIEND_PARAM}=${token}`);
}

/** Pull a friend token out of a Seabearing QR payload, or null if it isn't one. */
export function parseFriendLinkPayload(text: string): string | null {
  const t = (text || '').trim();
  if (!t) return null;
  const p = new URLSearchParams(t);
  const tok = p.get(FRIEND_PARAM)?.trim();
  return tok && TOKEN_RE.test(tok) ? tok : null;
}

/** If this page was opened from a friend link, stash its token and drop it from the address bar.
 *  Safe to call on every load; never throws. */
export function captureFriendLink(): void {
  try {
    const payload = pendingSeabearingJoinPayload();
    const token = payload ? parseFriendLinkPayload(payload)?.slice(0, 256) : null;
    if (token) stashFriendToken(token);
    if (token) clearPendingSeabearingJoinPayload(payload ?? undefined);
  } catch {
    /* no history API / storage — the link just isn't remembered across the login wall */
  }
}

/** Remember a friend token until the user has answered it (also used by the in-app QR scanner). */
export function stashFriendToken(token: string): void {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify({ token, at: Date.now() })); } catch { /* private mode */ }
}

/** The friend token still waiting for an answer, if any. */
export function pendingFriendToken(): string | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as { token?: unknown; at?: unknown };
    if (typeof p?.token !== 'string' || !p.token || typeof p.at !== 'number' || Date.now() - p.at > PENDING_TTL_MS) {
      localStorage.removeItem(PENDING_KEY);
      return null;
    }
    return p.token;
  } catch {
    return null;
  }
}

/** Forget the stashed token (only if it is still `token`, when given — a newer link wins). */
export function clearPendingFriendToken(token?: string): void {
  try {
    if (token && pendingFriendToken() !== token) return;
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}
